import {IllegalStateException, InvalidArgumentException} from "../../errors/Exceptions.js";
import {TmHeadTransitionDirection} from "./TmTransitionRule.js";
import {TmState} from "./TmState.js";
import {
    containsArrayByMethod, containsEqualElement,
    getRandomInt,
    insertIntoArray,
    isString, removeRepeatsAndEmptyItems
} from "../../utils.js";
import {TmAlphabet} from "./TmAlphabet.js";
import {TmTransitionsRelation} from "./TmTransitionsRelation.js";
import {TmMemento} from "./TmMemento";


/**
 * Class for Turing machine
 */
export class TuringMachine{
    // definition of TM
    #states = []; // Q, TmState[] // each state is unique by name
    #alphabet; // T, TmAlphabet
    #emptyCellChar; // string
    #transitionsRelation; // TmTransitionsRelation
    #initialState; // q_0, TmState
    #finalStates; // F (q^*), TmState[]
    // TM should stop when final state is reached

    // current state of TM
    #currentState; // TmState
    #tape; // string[]
    #currentHeadPosition; // number, index of head in tape
    #executedStepsCount = 0;



    /**
     * Creates a Turing machine with given definition and sets the tape content.
     *
     * If given <code>states</code> array contains equal states (according to <code>TmState::equals()</code> method),
     * only the first of them is considered.
     *
     * @param states {TmState[]} must be non-empty
     * @param alphabet {TmAlphabet}
     * @param transitionsRelation {TmTransitionsRelation} must correspond to <code>states</code> and <code>alphabet</code>
     * @param initialState {TmState} must be present in <code>states</code> array
     * @param finalStates {TmState[]} each element of it must be present in <code>states</code> array
     * @param inputWord {string} is word to be put on tape. Must have length <code>>0</code>.
     * Each character of it must be contained in <code>this.#alphabet</code>.
     * Must contain at least one non-empty-cell character.
     */
    constructor(states, alphabet, transitionsRelation,
                initialState, finalStates, inputWord) {
        this.#initStates(states);

        // init initialState
        if(!containsEqualElement(this.#states, initialState)){
            throw new InvalidArgumentException("states must contain initialState");
        } // now it is guaranteed that initialState is TmState
        this.#initialState = initialState;

        this.#initFinalStates(finalStates);

        // init alphabet
        if(!(alphabet instanceof TmAlphabet)){
            throw new InvalidArgumentException("alphabet must be a TmAlphabet object");
        }
        this.#alphabet = alphabet;
        this.#emptyCellChar = alphabet.emptyCellChar;

        // init transitionsRelation
        if(!(transitionsRelation instanceof TmTransitionsRelation)){
            throw new InvalidArgumentException("transitionsRelation must be a TmTransitionsRelation object");
        }
        if(!this.#alphabet.includesAll(transitionsRelation.usedChars)){
            throw new InvalidArgumentException("each char that is used in transitionsRelation must be contained in this.#alphabet");
        }
        if(!containsArrayByMethod(this.#states, transitionsRelation.usedStates)){
            throw new InvalidArgumentException("each state that is used in transitionsRelation must be contained in this.#states");
        }
        this.#transitionsRelation = transitionsRelation;

        // init current state
        this.#currentState = this.#initialState;
        this.#initTapeAndHeadPosition(inputWord);
        // this.#executedStepsCount is initialized in class definition
    }


    /**
     * Initializes <code>this.#states</code> property.
     *
     * If given array contains equal states (according to <code>TmState::equals()</code> method),
     * only the first of them is considered.
     * @param states {TmState[]} must be non-empty.
     */
    #initStates(states){
        if(!Array.isArray(states)){
            throw new InvalidArgumentException("states must be an Array");
        }

        let statesFiltered = removeRepeatsAndEmptyItems(states);
        if(statesFiltered.length === 0){ // can compare by length, because statesFiltered has not empty items
            throw new InvalidArgumentException("states array can't be empty");
        }

        for (const state of statesFiltered){
            if(!(state instanceof TmState)){
                throw new InvalidArgumentException(`each element of states array must be a TmState object`);
            }
        }

        this.#states = statesFiltered;
    }

    /**
     * Initializes <code>this.#finalStates</code> property.
     *
     * If given array contains equal states (according to <code>TmState::equals()</code> method),
     * only the first of them is considered.
     *
     * **Attention:** to use this method, <code>this.#states</code> must be already initialized.
     * @param finalStates {TmState[]} must be non-empty.
     */
    #initFinalStates(finalStates){
        if(!Array.isArray(finalStates)){
            throw new InvalidArgumentException("finalStates must be an Array");
        }

        let finalStatesFiltered = removeRepeatsAndEmptyItems(finalStates);
        if(finalStatesFiltered.length === 0){ // can compare by length, because finalStatesFiltered has not empty items
            throw new InvalidArgumentException("finalStates array can't be empty");
        }

        for (const state of finalStatesFiltered){
            if(!(state instanceof TmState)){
                throw new InvalidArgumentException(`each element of finalStates array must be a TmState object`);
            }
            if(!containsEqualElement(this.#states, state)){
                throw new InvalidArgumentException('this.#states must contain each state from finalStates');
            }
        }

        this.#finalStates = finalStatesFiltered;
    }

    /**
     * Puts <code>inputWord</code> on tape, and puts head on first non-empty-cell
     * character of <code>inputWord</code> -- Initializes <code>this.#tape</code> and <code>this.#currentHeadPosition</code>.
     *
     * **Attention!** Before calling this method, <code>this.#alphabet</code> and
     * <code>this.#emptyCellChar</code> must be already initialized.
     * @param inputWord {string} is word to be put on tape. Must have length <code>>0</code>.
     * Each character of it must be contained in <code>this.#alphabet</code>.
     * Must contain at least one non-empty-cell character.
     */
    #initTapeAndHeadPosition(inputWord){
        // validation
        if(!isString(inputWord)){
            throw new InvalidArgumentException("inputWord must be a string");
        }
        if(inputWord.length < 1){
            throw new InvalidArgumentException("inputWord must be not-empty");
        }
        // here, inputWord contains at least one character

        let containsNonEmptyCharacter = false;
        for (let char of inputWord) {
            if(!this.#alphabet.includes(char)){
                throw new InvalidArgumentException(
                    `each char of inputWord must be included into alphabet (caused by char '${char}')`);
            }
            if(char !== this.#emptyCellChar){
                containsNonEmptyCharacter = true;
            }
        }
        // if(!containsNonEmptyCharacter){
        //     throw new InvalidArgumentException("inputWord must contain at least one non-empty-cell character");
        // }

        // initialize this.#tape
        this.#tape = Array.from(inputWord);

        // initialize this.#currentHeadPosition

        if(!containsNonEmptyCharacter){ // inputWord contains only empty-cell chars
            this.#currentHeadPosition = 0;
        }
        else{
            // find position of first non-empty-cell char
            let idx = 0;
            while(idx < this.#tape.length && this.#tape[idx] === this.#emptyCellChar){
                idx++;
            } // it is guaranteed, that idx now isn't out of bounds, because non-empty-cell char exists in inputWord
            this.#currentHeadPosition = idx;
        }
    }

    /**
     *
     * @return {TmState[]}
     */
    get states(){
        return this.#states;
    }

    /**
     *
     * @return {TmState}
     */
    get initialState(){
        return this.#initialState;
    }

    /**
     *
     * @return {TmState[]}
     */
    get finalStates(){
        return this.#finalStates;
    }

    /**
     *
     * @return {TmAlphabet}
     */
    get alphabet(){
        return this.#alphabet;
    }

    /**
     *
     * @return {string}
     */
    get emptyCellChar(){
        return this.#emptyCellChar;
    }

    /**
     *
     * @return {TmTransitionsRelation}
     */
    get transitionsRelation(){
        return this.#transitionsRelation;
    }

    /**
     *
     * @return {TmState}
     */
    get currentState(){
        return this.#currentState;
    }

    /**
     *
     * @return {string[]}
     */
    get tape(){
        return this.#tape;
    }

    /**
     *
     * @return {number}
     */
    get currentHeadPosition(){
        return this.#currentHeadPosition;
    }

    /**
     *
     * @return {number}
     */
    get executedStepsCount(){
        return this.#executedStepsCount;
    }

    /**
     *
     * @return {string} character from the cell where the head is looking on.
     */
    get lookedChar(){
       return this.#tape[this.#currentHeadPosition];
    }

    /**
     * Checks if TM is finished, i.e. if current state is final
     * @return {boolean}
     */
    isFinished() {
        return containsEqualElement(this.#finalStates, this.#currentState);
    }

    /**
     * Checks of TM is stuck, i.e. if no transition rule is available (except TM is finished)
     * @return {boolean}
     */
    isStuck(){
        let availableTransitionRules = this.#transitionsRelation
            .getPossibleTransitionRules(
                this.currentState, this.lookedChar);
        return !this.isFinished() && !availableTransitionRules; // not finished, and no available transition rules
    }

    /**
     * Checks if only one transition rule exists for each <code>state</code> and <code>lookedChar</code>.
     * @return {boolean}
     */
    isDeterministic(){
        return this.#transitionsRelation.isDeterministic();
    }

    /**
     * Moves head along the tape by one cell according to given <code>direction</code>.
     * If head becomes out of bounds of <code>this.#tape</code> array, expands <code>this.#tape</code>
     * by <code>this.#emptyCellChar</code>,
     * so <code>this.#currentHeadPosition</code> is valid index.
     *
     * It **changes following properties**: <code>this.#currentHeadPosition</code>, <code>this.#tape</code>.
     * @param direction is one of the following: <code>TmHeadTransitionDirection.LEFT</code>,
     * <code>TmHeadTransitionDirection.RIGHT</code>, <code>TmHeadTransitionDirection.NONE</code>
     */
    #moveHead(direction){
        let newHeadPosition;
        switch(direction){
            case TmHeadTransitionDirection.LEFT:
                newHeadPosition = this.#currentHeadPosition - 1;
                break;
            case TmHeadTransitionDirection.RIGHT:
                newHeadPosition = this.#currentHeadPosition + 1;
                break;
            case TmHeadTransitionDirection.NONE:
                newHeadPosition = this.#currentHeadPosition;
                break;
            default:
                throw new InvalidArgumentException("direction must be a valid TmHeadTransitionDirection");
        }

        // check if newHeadPosition is out of bounds, and expand tape accordingly
        if(newHeadPosition === -1){ // < 0 is not possible to be, because moving step is 1
            this.#tape = insertIntoArray(this.#tape, 0, this.#emptyCellChar);
            newHeadPosition = 0;
        }
        else if(newHeadPosition === this.#tape.length){ // > this.#tape.length is not possible to be, because moving step is 1
            this.#tape.push(this.#emptyCellChar);
        }

        this.#currentHeadPosition = newHeadPosition;
    }

    /**
     * Chooses suitable TransitionRule (according to <code>currentState</code> and <code>lookedChar</code>),
     * performs this transition, and makes ALL necessary changes of properties.
     * If no transition exists, throws <code>IllegalStateException</code>.
     *
     * If TM is non-deterministic, TransitionRule is chosen by random from available ones.
     *
     * This method DOES NOT check if TM is finished -- so if
     * transition from finish state exists, this transition will be performed.
     * To adhere to finishing-safety, use <code>executeNextStep()</code> public-method instead.
     *
     */
    #executeNextStepImpl(){
        let lookedChar = this.#tape[this.#currentHeadPosition];
        let possibleTransitionRules =
            this.#transitionsRelation.getPossibleTransitionRules(this.#currentState, lookedChar); // array of transition rules

        // choose rule to execute
        let rule;
        if(possibleTransitionRules === undefined){
            throw new IllegalStateException("there is no possible transition rules");
        }
        if(possibleTransitionRules.length === 1){
            rule = possibleTransitionRules[0];
        }
        else if(possibleTransitionRules.length > 1){ // transition is non-deterministic, need to choose rule
            let ruleIndex = getRandomInt(0, possibleTransitionRules.length);
            rule = possibleTransitionRules[ruleIndex];
        }

        // execute rule
        this.#currentState = rule.toState;
        this.#tape[this.#currentHeadPosition] = rule.charToWrite;
        this.#moveHead(rule.headDirection); // move head
        this.#executedStepsCount++;
    }

    /**
     * Chooses suitable TransitionRule (according to <code>currentState</code> and <code>lookedChar</code>),
     * performs this transition.
     * If no transition exists or TM is finished, throws <code>IllegalStateException</code>.
     *
     * If TM is non-deterministic, TransitionRule is chosen by random from available ones.
     *
     */
    executeNextStep(){
        if(this.isFinished()){
            throw new IllegalStateException("Can't execute next step when TM is already finished");
        }

        this.#executeNextStepImpl();
    }

    /**
     * If not finished, executes steps until final state is reached, or until <code>limitOfSteps</code> reached.
     *
     * If already finished, throws <code>IllegalStateException</code>.
     * @param limitOfSteps {number} is maximum number of commands allowed to be executed by this method.
     * It is recommended to set <code>limitOfSteps</code> to avoid exceptions. Must be positive number.
     */
    executeStepsUntilTheEnd(limitOfSteps){
        if(limitOfSteps !== undefined && limitOfSteps !== null && limitOfSteps < 1){
            throw new InvalidArgumentException("limitOfSteps must be positive");
        }

        if(this.isFinished()){
            throw new IllegalStateException("Can't execute rest of steps, because TM is already finished");
        }

        let steps = 0;
        while(!this.isFinished() && (!limitOfSteps || steps < limitOfSteps)){
            this.#executeNextStepImpl();
            steps++;
        }
    }

    /**
     * Goes to <code>initialState</code>, executes steps until final state is reached,
     * or until <code>limitOfSteps</code> reached.
     * <code>executedStepsCount</code> is reset to zero before execution.
     *
     * Tapes content and headPosition is current (not initial).
     * @param limitOfSteps {number} is maximum number of commands allowed to be executed by this method.
     * It is recommended to set <code>limitOfSteps</code> to avoid exceptions. Must be positive number.
     */
    executeFromInitialStateUntilTheEnd(limitOfSteps){
        if(limitOfSteps !== undefined && limitOfSteps !== null && limitOfSteps < 1){
            throw new InvalidArgumentException("limitOfSteps must be positive");
        }

        this.#currentState = this.#initialState;
        this.#executedStepsCount = 0;

        let steps = 0;
        while(!this.isFinished() && (!limitOfSteps || steps < limitOfSteps)){
            this.#executeNextStepImpl();
            steps++;
        }
    }

    /**
     * Resets the Turing machine:
     * <ul>
     *     <li>state is reset to <code>initialState</code></li>
     *     <li>counter of executed steps is reset to 0</li>
     *     <li><code>inputWord</code> is put on tape (init <code>this.#tape</code>)</li>
     *     <li>head is put on first non-empty-cell character of <code>inputWord</code>
     *     (init <code>this.#currentHeadPosition</code>)</li>
     * </ul>
     *
     * @param inputWord {string} is word to be put on tape. Must have length <code>>0</code>.
     * Each character of it must be contained in <code>this.#alphabet</code>.
     * Must contain at least one non-empty-cell character.
     */
    reset(inputWord){
        this.#currentState = this.#initialState;
        this.#executedStepsCount = 0;

        this.#initTapeAndHeadPosition(inputWord);
    }

    /**
     * Returns word (<code>string</code>), that is on the tape.
     *
     * "Word" means the sequence of characters on tape, from leftmost non-empty-cell character
     * to the rightmost one (inclusively).
     *
     * If there are only empty-cell characters on the tape, empty string will be returned.
     * @return {string}
     */
    getWordFromTape(){
        let leftmostNonEmptyCharIndex = undefined;
        let rightmostNonEmptyCharIndex = undefined;

        // find leftmost
        let idx = 0;
        while(idx < this.#tape.length && this.#tape[idx] === this.#emptyCellChar){
            idx++;
        }
        if(idx === this.#tape.length){
            return ""; // no non-empty-cell character on tape
        }
        leftmostNonEmptyCharIndex = idx;

        // find rightmost
        idx = this.#tape.length - 1;
        while(idx > -1 && this.#tape[idx] === this.#emptyCellChar){
            idx--;
        } // no need to check if idx is -1, because at least we have leftmost non-empty-cell char
        rightmostNonEmptyCharIndex = idx;

        return this.#tape
            .slice(leftmostNonEmptyCharIndex, rightmostNonEmptyCharIndex + 1)
            .join('');
    }

    /**
     * Converts tape to string
     *
     * @return {string} tape as string with caret (<code>^</code>) marker below that shows current head position
     */
    getTapeAsString(){
        let str = "";

        str += this.#tape.join('') + '\n'; // concatenate cells to one string
        str += ' '.repeat(this.#currentHeadPosition) + '^';

        return str;
    }

    /**
     * Creates a copy of its state
     * @return {TmMemento}
     */
    saveState(){
        return new TmMemento(
            this.currentState,
            [...this.tape],
            this.currentHeadPosition,
            this.executedStepsCount
        );
    }

    /**
     * Sets the current TM state to the given <code>state</code>.
     *
     * @param state {TmMemento} must be compatible with the states, alphabet and word, that initialize the Turing machine
     */
    restoreState(state){
        this.#currentState = state.currentState;
        this.#tape = [...state.tape];
        this.#currentHeadPosition = state.currentHeadPosition;
        this.#executedStepsCount = state.executedStepsCount;
    }

    /**
     * Gives  string representation of TM.
     * @return {string}
     */
    toString(){
        let str = "";

        // definition
        str += `Alphabet: ${this.#alphabet.toString()}\n`;
        str += `States: [${this.#states.map((s) => s.toShortString()).join(", ")}]\n`;
        str += `Initial state: ${this.#initialState.toShortString()}\n`;
        str += `Final states: [${this.#finalStates.map((s) => s.toShortString()).join(", ")}]\n`;
        str += `Is deterministic: ${this.isDeterministic()}\n\n`;

        str += `Transitions relation:\n${this.#transitionsRelation.toString()}\n\n`;

        // current state
//        str += `Current state: ${this.#currentState.toShortString()}\n`;
//        str += `Is finished: ${this.isFinished()}\n`;
//        str += `Head position: ${this.#currentHeadPosition}\n`;
//        str += `Executed steps: ${this.#executedStepsCount}\n\n`;

//        str += `Tape:\n`;
//        str += this.getTapeAsString();
        return str;
    }
}


/*
NOTES

if some transition does not exist, then we have endless loop when we'll make total the transitions relation
 */





